import type { Ref } from 'react'

import { cn } from '#lib/cn'

/**
 * Viewport-level stacking order: overlays, floating elements nested in an
 * overlay, then toasts above both. Full class names so Tailwind generates them.
 */
export const OverlayLayer = {
  Base: 'z-50',
  Nested: 'z-[51]',
  Toast: 'z-[60]',
} as const

/**
 * Backdrop overlay shared by Dialog, Drawer, and other overlay components.
 *
 * Animation matches the paired popup (Dialog popup, Drawer surface): same
 * 350ms-in / 250ms-out duration and the default transition curve. Paired
 * elements must animate as a unit: if the backdrop resolves before the
 * popup, the overlay feels disjointed.
 */
export const backdropStyles = [
  'fixed inset-0',
  OverlayLayer.Base,
  'bg-black/20 dark:bg-black/50',
  // Opacity is not vestibular-triggering, so no motion-safe guard:
  // this matches the popup's always-on opacity transition under
  // reduced motion so they still resolve as a pair.
  'transition-opacity duration-350',
  'data-[starting-style]:opacity-0',
  'data-[ending-style]:opacity-0',
  'data-[ending-style]:duration-250',
]

/**
 * Backdrop styles for an overlay scoped to a parent container instead of the
 * viewport. Used when the overlay is portalled into a positioned ancestor
 * (e.g. a proposal area) so the scrim is bounded to that ancestor and the
 * surrounding chrome stays visible and interactive.
 *
 * Animations match {@link backdropStyles} — only the positioning differs.
 */
export const scopedBackdropStyles = [
  // z-50 matches the global backdrop. Since scoped overlays render inside
  // their container's local stacking context, z-50 here only competes with
  // descendants of that container — not with anything at the page level.
  'absolute inset-0 z-50',
  'bg-black/20 dark:bg-black/50',
  'transition-opacity duration-350',
  'data-[starting-style]:opacity-0',
  'data-[ending-style]:opacity-0',
  'data-[ending-style]:duration-250',
]

/**
 * Portal container for nested floating elements (Select, Menu, etc.).
 *
 * By default, floating elements like Select portal their dropdown to <body>.
 * When inside an overlay, this puts the dropdown in a separate FloatingPortal div
 * at body level - on the same layer as the overlay viewport, causing the dropdown
 * to render behind the overlay surface.
 *
 * This container is rendered as a sibling of the viewport inside the overlay's own
 * FloatingPortal. On `OverlayLayer.Nested` it sits one level above the overlay surface.
 * Select.Content reads it via PortalContainerContext and portals into it instead
 * of body, keeping the dropdown above the overlay.
 *
 * pointer-events-none prevents the full-screen overlay from blocking interaction.
 * [&>*]:pointer-events-auto re-enables events on the FloatingPortal child div
 * so the dropdown remains clickable.
 *
 * empty:hidden ensures the div has zero cost when no floating element is portaled
 * into it. When a Select dropdown portals in, :empty no longer matches and the
 * container becomes visible with the correct stacking.
 */
export function OverlayPortalContainer({ ref }: { ref: Ref<HTMLDivElement> }) {
  return (
    <div
      ref={ref}
      // A modal overlay aria-hides everything outside it except Base UI portals.
      data-base-ui-portal=""
      className={cn(
        'empty:hidden',
        'fixed inset-0',
        OverlayLayer.Nested,
        'pointer-events-none [&>*]:pointer-events-auto',
      )}
    />
  )
}
OverlayPortalContainer.displayName = 'OverlayPortalContainer'
